import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import SchedulePanel from './SchedulePanel';
import SourcePanel from './SourcePanel';
import SourceForm from './SourceForm';
import { Row, Col, Container, Card } from 'react-bootstrap';
import { VscDatabase } from 'react-icons/vsc'; 
import {
  getConnectionData,
  saveDataSource,
  testDataSource,
  deleteDataSource
} from 'api';
import { getErrorHandler } from 'Utils'
import { DataSourceContext } from './Utils';
import { needsCredentials, FILE_SOURCE_TYPE, vfsSchemeToTypeOption, parseAzblobPath } from './datasourcesApi';
import { startProfileJob } from '../Metadata/profileApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import AppContext from 'Utils/AppContext';
import { FaRegThumbsUp, FaConnectdevelop } from 'react-icons/fa';
import DiscoveryTooltip from 'DiscoveryTooltip';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import RunDiscoveryButton from 'Utils/RunDiscoveryButton';
import SubSourcesPanel from 'DataSources/SubSourcesPanel';
import './DataSource.css';

/**
 * Decode `?prefill=<base64-json>` into the connection shape the editor expects.
 *
 * Two payload shapes are supported:
 *  - File sources (`type` starts with `FILE_`): the document-corpus overview and the
 *    Undiscovered page's "Create Data Source" link populate platform + file type + path
 *    + (optional) delimiter. Mapped into the FileSourceEditor shape.
 *  - Database / NoSQL sources (`type` is an engine string, e.g. `postgresql`, `mongodb`):
 *    the Undiscovered page's "Create Data Source" link on a manager-surfaced relational
 *    or server resource. `type` carries the engine the DB form binds to; host/port/etc.
 *    pre-fill the fields, password is left blank for the user.
 *
 * Silently returns null on any decode failure — the editor falls back to an empty form.
 */
function decodePrefill(search) {
  if (!search) return null;
  const raw = new URLSearchParams(search).get('prefill');
  if (!raw) return null;
  try {
    const decoded = JSON.parse(atob(raw));
    if (!decoded || typeof decoded !== 'object') return null;
    if (typeof decoded.type !== 'string' || !decoded.type) return null;

    const isFileType = decoded.type.startsWith('FILE_');
    if (isFileType) {
      const scheme = decoded.vfsScheme || 'LOCAL';
      return {
        type: FILE_SOURCE_TYPE,
        customDatabaseType: vfsSchemeToTypeOption(scheme),
        vfsScheme: scheme,
        fileType: decoded.type,
        rootPath: decoded.rootPath || '',
        name: decoded.name || '',
        region: decoded.region || '',
        delimiter: decoded.delimiter || (decoded.type === 'FILE_DELIMITED_FOLDER' ? ',' : ''),
        // Discovery scope prefill (folder drill-down page): recurse defaults true,
        // filter blank = all supported types.
        recurse: decoded.recurse !== false,
        fileNameFilter: decoded.fileNameFilter || '',
        fileNameExcludeFilter: decoded.fileNameExcludeFilter || '',
      };
    }

    // Database / NoSQL prefill — `type` is the engine string the DB form binds to.
    return {
      type: decoded.type,
      name: decoded.name || '',
      host: decoded.host || '',
      port: decoded.port != null ? decoded.port : '',
      database: decoded.database || '',
      schemaName: decoded.schemaName || '',
      user: decoded.user || '',
      password: '',
      region: decoded.region || '',
    };
  } catch (e) {
    return null;
  }
}

const SourceEditor = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const prefill = useMemo(() => decodePrefill(location.search), [location.search]);
  const [connection, setConnection] = useState(prefill || {});
  const [error, setError] = useState('');
  const [fetchingData, setFetchingData] = useState(false);
  const [currentState, setCurrentState] = useState(null);
  const [success, setSuccess] = useState(false);
  const [connectionTest, setConnectionTest] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetched, setFetched] = useState(false);
  const { loadConnectionsHandler, fetchNewEntitlements, setNotification } = useContext(AppContext);
  const handleError = getErrorHandler(setError);
  const context = {
    fetched,
    connection,
    setConnection,
    success,
    setSuccess,
    fetchingData,
    currentState,
    setUnsavedChanges,
    saving
  };

  useEffect(() => {
    if (!id) {
      setFetched(true);
      return;
    }
    let cancelled = false;
    setFetched(false);
    const fetch = async (connectionName) => {
      if (!saving) setCurrentState('fetching');
      try {
        await getConnectionDataHandler(connectionName);
      } catch (e) {
        handleError(e);
      }
      if (!cancelled) {
        setFetched(true);
        setCurrentState(null);
      }
    };
    fetch(id);
    return () => {
      cancelled = true;
    };
  }, [id]);

  const getConnectionDataHandler = async (connection) => {
    const connectionValue = await getConnectionData(connection);
    setConnection(connectionValue);
    if (connectionValue.connectionTest) {
      setConnectionTest(connectionValue.connectionTest);
    } else {
      setConnectionTest({});
    }
  }

  // Azure Blob sources need BOTH a storage account and a container — supplied either via the
  // dedicated fields or encoded in the rootPath (azblob://account/container/). Block save/test
  // until that's satisfied, with a clear message, rather than letting it fail server-side.
  const azureBlobValidationError = (conn) => {
    if (!conn || conn.vfsScheme !== 'AZURE_BLOB') return null;
    const parsed = parseAzblobPath(conn.rootPath);
    const account = String(conn.account || parsed.account || '').trim();
    const container = String(conn.container || parsed.container || '').trim();
    if (!account || !container) {
      return 'Azure Blob sources require a storage account and a container. Fill in both fields, '
        + 'or include them in the root path as azblob://<account>/<container>/.';
    }
    return null;
  };

  // SharePoint sources need a sharepoint://hostname/.../library rootPath plus the Entra app
  // registration ids and one credential proof. Mirrors validateSharePointRequirements
  // server-side — except for the proof on an EXISTING row: stored secrets live in
  // encrypted_secrets and are stripped from the GET response entirely (not even echoed as
  // "****"), so the client cannot see whether a proof is on file. The server merges the
  // stored proof into the update before validating, so only a brand-new row (no id yet)
  // must carry a proof in the form; an existing row is validated server-side.
  const sharePointValidationError = (conn) => {
    if (!conn || conn.vfsScheme !== 'SHAREPOINT') return null;
    const rp = String(conn.rootPath || '').trim();
    const m = /^sharepoint:\/\/([^/]+)\/(.+)$/i.exec(rp);
    const segs = m && m[1].includes('.') ? m[2].split('/').filter(Boolean) : null;
    const hasLibrary = segs
      && (/^(sites|teams)$/i.test(segs[0]) ? segs.length >= 3 : segs.length >= 1);
    if (!hasLibrary) {
      return 'SharePoint sources need a root path like '
        + 'sharepoint://contoso.sharepoint.com/sites/HR/Documents (hostname and document '
        + 'library are required; the site segment is optional for the root site).';
    }
    if (!String(conn.tenantId || '').trim() || !String(conn.clientId || '').trim()) {
      return 'SharePoint sources require the Entra tenant ID and client ID.';
    }
    const isExistingRow = conn.id != null;
    const hasSecret = !!String(conn.clientSecret || '').trim();
    const hasCert = !!(String(conn.certificatePem || '').trim()
      && String(conn.certificatePrivateKey || '').trim());
    if (!isExistingRow && !hasSecret && !hasCert) {
      return 'SharePoint sources require a credential proof — a client secret, or a '
        + 'certificate and private key.';
    }
    return null;
  };

  const fileSchemeValidationError = (conn) =>
    azureBlobValidationError(conn) || sharePointValidationError(conn);

  const saveDataSourceHandler = async (connection) => {
    const azErr = fileSchemeValidationError(connection);
    if (azErr) {
      setError(azErr);
      return;
    }
    setSaving(true);
    try {
      const saved = await saveDataSource(connection, id);
      await loadConnectionsHandler();
      if (id) {
        // Editing an existing row: URL doesn't change, so the route useEffect won't
        // refire — refetch manually so the form picks up server-side normalization.
        await getConnectionDataHandler(id);
      } else {
        // New row: navigate to the saved row's id-based URL. The route :id change
        // triggers the useEffect which fetches and populates the form (also flips on
        // the Run Discovery button since `id && fetched` becomes true).
        const savedId = saved && saved.id != null ? saved.id : null;
        if (savedId != null) {
          history.push('/sources/source-editor/' + encodeURIComponent(savedId));
        }
      }

      setNotification({
        header: 'Data source saved!',
        icon: (<FaRegThumbsUp size='25px' color='#16A34A' />),
        body: <span>Data source successfully saved.</span>
      });
    } catch(e) {
      handleError(e);
    }
    setSaving(false);
  }

  const deleteDataSourceHandler = async (source) => {
    setCurrentState('deleting');
    try {
      await deleteDataSource(source);
      await loadConnectionsHandler();
      await fetchNewEntitlements();
      history.push('/sources');
    } catch(e) {
      handleError(e);
    }
    setCurrentState(null);
  }

  const testConnectionHandler = async () => {
    setError(null);
    const azErr = fileSchemeValidationError(connection);
    if (azErr) {
      setError(azErr);
      return;
    }
    setCurrentState('testing');
    try {
      let lookupId = id || connection.id || null;
      if (unsavedChanges) {
        const saved = await saveDataSource(connection, id);
        await loadConnectionsHandler();
        const savedId = saved && saved.id != null ? saved.id : null;
        if (!id && savedId != null) {
          lookupId = savedId;
          history.push('/sources/source-editor/' + encodeURIComponent(savedId));
        } else if (savedId != null) {
          lookupId = savedId;
        }
      }
      if (lookupId == null) {
        throw new Error('Save the data source before testing the connection.');
      }
      const refreshed = await getConnectionData(lookupId);
      if (!refreshed || refreshed.id == null) {
        throw new Error('Data source not found. Save the data source and try again.');
      }
      setConnection(refreshed);
      const summary = await testDataSource(refreshed);
      setConnectionTest(summary);
      await loadConnectionsHandler();
    } catch(e) {
      handleError(e);
    }
    setCurrentState(null);
  }

  // Start a profile (schema/file-folder metadata) run for this source. Mirrors the
  // test handler's "save first so an id exists" flow. Profile jobs run async; for file
  // sources we send the user to the metadata detail view, which polls until the job
  // completes and then renders the folder's total size + per-file listing. Relational
  // sources get a notification — their results appear in the Schema Metadata panel.
  const profileHandler = async () => {
    setError(null);
    const azErr = azureBlobValidationError(connection);
    if (azErr) {
      setError(azErr);
      return;
    }
    setCurrentState('profiling');
    try {
      let lookupId = id || connection.id || null;
      if (unsavedChanges) {
        const saved = await saveDataSource(connection, id);
        await loadConnectionsHandler();
        const savedId = saved && saved.id != null ? saved.id : null;
        if (!id && savedId != null) {
          lookupId = savedId;
          history.push('/sources/source-editor/' + encodeURIComponent(savedId));
        } else if (savedId != null) {
          lookupId = savedId;
        }
      }
      if (lookupId == null) {
        throw new Error('Save the data source before running a profile.');
      }
      // Pre-run gate: the source needs a successful connection test (a fresh one is run
      // when none is on record). We're already on the editor, so surface the failure
      // inline instead of navigating.
      const gate = await ensureConnectionTested(Number(lookupId), { navigateOnFail: false });
      if (!gate.ok) {
        throw new Error(`Connection test failed: ${gate.message || 'the data source is unreachable.'} Fix the connection and try again.`);
      }
      const job = await startProfileJob(Number(lookupId));
      setNotification({
        header: 'Profile started',
        body: (
          <span>
            Profile run started{job && job.id ? ` (job ${job.id})` : ''}. Results appear in the
            Schema Metadata panel when it completes.
          </span>
        ),
      });
      if (connection.type === FILE_SOURCE_TYPE) {
        history.push('/metadata/source/' + encodeURIComponent(lookupId));
      }
    } catch (e) {
      handleError(e);
    }
    setCurrentState(null);
  }

  return (
    <div className='p-0 h-100'>
      <FetchContainer fetchingData={currentState === 'fetching'} absolute={true}>
        <PageHeader
          header='Data source editor'
          tooltip='Create or edit a data source to run discoveries.'
          icon={FaConnectdevelop}
          // Edit mode only: a new unsaved source has no id and no risk profile to link to.
          meta={id && connection.name ? (
            <DiscoveryTooltip placement='bottom' text='View this data source&#39;s risk overview'>
              <Link to={`/landscape/riskprofile/${encodeURIComponent(id)}`}>
                {connection.name}
              </Link>
            </DiscoveryTooltip>
          ) : null}
        />
        <DataSourceContext.Provider value={context}>
          <div>
            <Row className='widget-row'>
              <Col lg='6' >
                {id && fetched && !needsCredentials(connection) && (
                  <Card className='shadow-sm widget-card mb-4'>
                    <div className='d-flex flex-row align-items-center p-3'>
                      <div className='flex-grow-1'>
                        Run discovery against this data source.
                      </div>
                      <RunDiscoveryButton
                        dataSource={connection}
                        onRunStarted={() => loadConnectionsHandler()}
                      />
                    </div>
                  </Card>
                )}
                <SourceForm
                  deleteDataSourceHandler={deleteDataSourceHandler}
                  saveDataSourceHandler={saveDataSourceHandler}
                  fetchingData={fetchingData}
                  currentState={currentState}
                />
              </Col>
              <Col lg='6'>
                <Row className='mb-4'>
                  <Col>
                    <SourcePanel
                      currentState={currentState}
                      error={error}
                      testConnectionHandler={testConnectionHandler}
                      profileHandler={profileHandler}
                      type={connection.type}
                      connectionTest={connectionTest}
                      connectionName={connection.name}
                    />
                  </Col>
                </Row>
                <Row className='mb-4'>
                  <Col>
                    <SchedulePanel />
                  </Col>
                </Row>
                {id && fetched && (
                  <Row>
                    <Col>
                      <SubSourcesPanel parentId={Number(id)} />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </div>
        </DataSourceContext.Provider>
      </FetchContainer>
    </div>
  );
}

export default SourceEditor;